import { useAppDispatch, useAppSelector } from "../../../Redux/hooks";
import AddNewAddress from "./AddNewAddress";
import { useEffect, useState } from "react";
import {
  CheckCircle2,
  Edit3,
  Home,
  Mail,
  MapPin,
  Phone,
  Plus,
  Trash2,
} from "lucide-react";
import {
  getAllAddresses,
  deleteAddress,
  setDefaultAddress,
  setCurrentAddress,
  clearCurrentAddress,
} from "../../../Redux/customer-address/slice";
import { Address } from "../../../Redux/customer-address/types";
import { toast } from "react-toastify";
import "./ManageAddressPage.css";

const ManageAddress = () => {
  const dispatch = useAppDispatch();
  const { user } = useAppSelector((state) => state.customerAuth);
  const { addresses, loading, error, currentAddress } = useAppSelector(
    (state) => state.customerAddress,
  );

  const [formOpen, setFormOpen] = useState(false);

  useEffect(() => {
    if (user && addresses.length === 0) {
      dispatch(getAllAddresses());
    }
  }, []);

  useEffect(() => {
    if (error) {
      toast.error(error, { autoClose: 3000 });
    }
  }, [error]);

  // While the form is open: lock body scroll on mobile and close on Escape.
  useEffect(() => {
    if (!formOpen) return;

    const mobileQuery = window.matchMedia("(max-width: 860px)");
    const previousBodyOverflow = document.body.style.overflow;

    const syncBodyScroll = () => {
      document.body.style.overflow = mobileQuery.matches
        ? "hidden"
        : previousBodyOverflow;
    };

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        closeForm();
      }
    };

    syncBodyScroll();
    mobileQuery.addEventListener("change", syncBodyScroll);
    window.addEventListener("keydown", handleKeyDown);

    return () => {
      document.body.style.overflow = previousBodyOverflow;
      mobileQuery.removeEventListener("change", syncBodyScroll);
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [formOpen]);

  const scrollToFormOnDesktop = () => {
    if (!window.matchMedia("(min-width: 861px)").matches) return;
    window.requestAnimationFrame(() => {
      document
        .getElementById("manage-address-form")
        ?.scrollIntoView({ behavior: "smooth", block: "nearest" });
    });
  };

  const openAddForm = () => {
    dispatch(clearCurrentAddress());
    setFormOpen(true);
    scrollToFormOnDesktop();
  };

  const openEditForm = (e: React.MouseEvent, address: Address) => {
    e.preventDefault();
    dispatch(setCurrentAddress(address));
    setFormOpen(true);
    scrollToFormOnDesktop();
  };

  const closeForm = () => {
    setFormOpen(false);
    dispatch(clearCurrentAddress());
  };

  const handleRemove = (e: React.MouseEvent, id?: string) => {
    e.preventDefault();
    if (id && window.confirm("Are you sure you want to remove this address?")) {
      if (currentAddress?._id === id) {
        closeForm();
      }
      dispatch(deleteAddress(id));
    }
  };

  const handleSetDefault = (id?: string) => {
    if (id) {
      dispatch(setDefaultAddress(id));
    }
  };

  return (
    <section className="manage-address-page">
      <div className="manage-address-shell">
        <div className="manage-address-page-heading">
          <div>
            <span className="manage-address-kicker">MY ACCOUNT</span>
            <h1>Manage Address</h1>
            <p>Save and manage your delivery addresses in one place.</p>
          </div>

          <div className="manage-address-summary">
            <MapPin size={16} strokeWidth={2} />
            <span>
              {addresses.length} saved{" "}
              {addresses.length === 1 ? "address" : "addresses"}
            </span>
          </div>
        </div>

        <div className="manage-address-grid">
          <button
            type="button"
            className={`manage-address-add-card ${formOpen && !currentAddress ? "active" : ""}`}
            onClick={openAddForm}
          >
            <span className="manage-address-add-icon">
              <Plus size={22} strokeWidth={2.4} />
            </span>
            <strong>Add Address</strong>
            <small>Add a new delivery address</small>
          </button>

          {loading && addresses.length === 0 && (
            <div className="manage-address-loading">Loading addresses...</div>
          )}

          {!loading && addresses.length === 0 && (
            <div className="manage-address-empty-note">
              No saved addresses yet — add your first delivery address.
            </div>
          )}

          {addresses.map((address) => (
            <article
              className={`manage-address-card ${address.defaultAddress ? "default" : ""}`}
              key={address._id}
            >
              <div className="manage-address-card-top">
                <div className="manage-address-card-label">
                  {address.defaultAddress ? (
                    <>
                      <CheckCircle2 size={15} strokeWidth={2.2} />
                      <strong>Default</strong>
                    </>
                  ) : (
                    <strong>{address.addressType || "Address"}</strong>
                  )}
                </div>
                <span className="manage-address-country">
                  <MapPin size={16} strokeWidth={1.9} />
                </span>
              </div>

              <div className="manage-address-card-body">
                <h2>{address.fullName}</h2>
                <p>{address.addressLine1}</p>
                {address.addressLine2 && <p>{address.addressLine2}</p>}
                {address.landmark && <p>Landmark: {address.landmark}</p>}
                <p>
                  {address.city}, {address.state} {address.pincode}
                </p>
                <p>{address.country}</p>

                <div className="manage-address-contact-row">
                  <Phone size={13} strokeWidth={2} />
                  <span>{address.phoneNumber}</span>
                </div>

                {address.email && (
                  <div className="manage-address-contact-row">
                    <Mail size={13} strokeWidth={2} />
                    <span>{address.email}</span>
                  </div>
                )}
              </div>

              <div className="manage-address-card-actions">
                <button
                  type="button"
                  onClick={(e) => openEditForm(e, address)}
                >
                  <Edit3 size={13} strokeWidth={2} />
                  Edit
                </button>

                <button
                  type="button"
                  className="danger"
                  onClick={(e) => handleRemove(e, address._id)}
                >
                  <Trash2 size={13} strokeWidth={2} />
                  Remove
                </button>

                {!address.defaultAddress && (
                  <button
                    type="button"
                    className="set-default"
                    onClick={() => handleSetDefault(address._id)}
                  >
                    <Home size={13} strokeWidth={2} />
                    Set as default
                  </button>
                )}
              </div>
            </article>
          ))}
        </div>

        {formOpen && (
          <button
            type="button"
            className="manage-address-mobile-backdrop"
            onClick={closeForm}
            aria-label="Close address form"
          />
        )}

        <div
          id="manage-address-form"
          className={`manage-address-form-panel ${formOpen ? "open" : ""}`}
        >
          {formOpen && (
            <AddNewAddress onSuccess={closeForm} onClose={closeForm} />
          )}
        </div>
      </div>
    </section>
  );
};

export default ManageAddress;
